/**
 * DiagramLabelEditor — inline edit panel for SVG workspace diagram labels.
 *
 * Shown below a diagram workspace when the activity sheet is in Edit mode.
 * Renders the appropriate label inputs for each workspace kind, writing
 * changes back to the problem's `wsProps` record via `onChange`.
 *
 * wsProps is a flat Record<string, string>; array props (e.g. stepLabels)
 * are stored as stepLabel0…stepLabel4 and reconstructed by the consumer.
 */

import React from 'react';
import type { WorkspaceKind } from './rendering-registry';

export interface DiagramLabelEditorProps {
  wsKind: WorkspaceKind;
  /** Current override props — empty object means "use defaults". */
  wsProps: Record<string, string>;
  onChange: (updated: Record<string, string>) => void;
  className?: string;
}

// ── Reusable label + input field ─────────────────────────────────────────────
function Field({
  label, fieldKey, wsProps, onChange, placeholder,
}: {
  label: string;
  fieldKey: string;
  wsProps: Record<string, string>;
  onChange: (updated: Record<string, string>) => void;
  placeholder?: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <label className="text-[11px] text-amber-700 font-medium w-28 flex-shrink-0 leading-tight">
        {label}
      </label>
      <input
        type="text"
        className="flex-1 px-2 py-1 text-xs rounded border border-amber-200 bg-white text-slate-700
                   focus:outline-none focus:ring-1 focus:ring-amber-300 focus:border-amber-300"
        value={wsProps[fieldKey] ?? ''}
        placeholder={placeholder}
        onChange={e => onChange({ ...wsProps, [fieldKey]: e.target.value })}
      />
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export function DiagramLabelEditor({
  wsKind, wsProps, onChange, className = '',
}: DiagramLabelEditorProps) {
  const wrap = (children: React.ReactNode, title: string) => (
    <div className={`mt-2 p-3 bg-amber-50/80 rounded-lg border border-amber-100 space-y-2 print:hidden ${className}`}>
      <p className="text-[10px] font-bold uppercase tracking-wide text-amber-600 mb-2">
        ✏️ {title}
      </p>
      {children}
    </div>
  );

  switch (wsKind) {
    case 'venn-diagram':
      return wrap(
        <>
          <Field label="Left circle" fieldKey="leftLabel" wsProps={wsProps} onChange={onChange} placeholder="Topic A" />
          <Field label="Right circle" fieldKey="rightLabel" wsProps={wsProps} onChange={onChange} placeholder="Topic B" />
          <Field label="Overlap label" fieldKey="bothLabel" wsProps={wsProps} onChange={onChange} placeholder="Both" />
        </>,
        'Venn Diagram Labels',
      );

    case 't-chart':
      return wrap(
        <>
          <Field label="Left column" fieldKey="leftLabel" wsProps={wsProps} onChange={onChange} placeholder="Column 1" />
          <Field label="Right column" fieldKey="rightLabel" wsProps={wsProps} onChange={onChange} placeholder="Column 2" />
        </>,
        'T-Chart Column Labels',
      );

    case 'story-map': {
      const defaults = ['Characters', 'Setting', 'Problem', 'Events', 'Solution'];
      return wrap(
        defaults.map((d, i) => (
          <Field key={i} label={`Box ${i + 1}`} fieldKey={`boxLabel${i}`} wsProps={wsProps} onChange={onChange} placeholder={d} />
        )),
        'Story Map Box Labels',
      );
    }

    case 'main-idea-web':
      return wrap(
        <Field label="Center topic" fieldKey="mainIdea" wsProps={wsProps} onChange={onChange} placeholder="Main Idea" />,
        'Main Idea Web',
      );

    case 'cer-organizer':
      return wrap(
        <>
          <Field label="Claim starter" fieldKey="claimStarter" wsProps={wsProps} onChange={onChange} placeholder="I believe that…" />
          <Field label="Evidence starter" fieldKey="evidenceStarter" wsProps={wsProps} onChange={onChange} placeholder="According to the text…" />
          <Field label="Reasoning starter" fieldKey="reasoningStarter" wsProps={wsProps} onChange={onChange} placeholder="This evidence shows…" />
        </>,
        'CER Sentence Starters',
      );

    case 'sequence-chain': {
      const seqDefaults = ['First', 'Next', 'Then', 'Finally', 'Last'];
      return wrap(
        seqDefaults.map((d, i) => (
          <Field key={i} label={`Step ${i + 1}`} fieldKey={`stepLabel${i}`} wsProps={wsProps} onChange={onChange} placeholder={d} />
        )),
        'Sequence Chain Step Labels',
      );
    }

    case 'sound-boxes':
      return wrap(
        <>
          <Field label="Word" fieldKey="word" wsProps={wsProps} onChange={onChange} placeholder="cat" />
          <Field label="Box count" fieldKey="phonemeCount" wsProps={wsProps} onChange={onChange} placeholder="3" />
        </>,
        'Sound Boxes',
      );

    case 'number-line':
      return wrap(
        <>
          <Field label="Start" fieldKey="start" wsProps={wsProps} onChange={onChange} placeholder="0" />
          <Field label="End" fieldKey="end" wsProps={wsProps} onChange={onChange} placeholder="10" />
          <Field label="Interval" fieldKey="interval" wsProps={wsProps} onChange={onChange} placeholder="1" />
        </>,
        'Number Line Range',
      );

    case 'fraction-strip':
      return wrap(
        <>
          <Field label="Numerator" fieldKey="numerator" wsProps={wsProps} onChange={onChange} placeholder="1" />
          <Field label="Denominator" fieldKey="denominator" wsProps={wsProps} onChange={onChange} placeholder="4" />
        </>,
        'Fraction Strip',
      );

    case 'number-bond':
      return wrap(
        <>
          <Field label="Whole" fieldKey="whole" wsProps={wsProps} onChange={onChange} placeholder="10" />
          <Field label="Part 1" fieldKey="part1" wsProps={wsProps} onChange={onChange} placeholder="6" />
          <Field label="Part 2" fieldKey="part2" wsProps={wsProps} onChange={onChange} placeholder="4" />
        </>,
        'Number Bond Values',
      );

    case 'ten-frame':
      return wrap(
        <Field label="Value (0–10)" fieldKey="value" wsProps={wsProps} onChange={onChange} placeholder="7" />,
        'Ten Frame',
      );

    default:
      return null;
  }
}
